import React, { useState } from 'react';
import { Col, Row, Container, Table, Input } from 'reactstrap';
import { AccentButton } from "../controls/AccentButton";
import { AccentSpinner } from "../controls/AccentSpinner";
import { AccentRadioUnboundCustom } from '../controls/unbound/AccentRadioUnbound';
import { AccentComboUnbound } from '../controls/unbound/AccentComboUnbound';
import { AccentTextAreaUnbound } from '../controls/unbound/AccentTextAreaUnbound';
import { useQueryAllNoTracking } from "../services/DataService";
import { accentUtils, from, showDialog, t } from "../services/HelperService";
import { jobLineStages } from '../services/JobService';
import { AccentDialog } from "./AccentDialog";


const CopyToDlg = ({ onClose, lines = [], sourceIndex }) => {
    const [sameOutcomeOnly, setSameOutcomeOnly] = React.useState(false); // leave OFF by default
    const [selected, setSelected] = React.useState({}); // { [index]: true }

    const source = lines[sourceIndex];
    const sourceOutcome = source?.Status ?? 'Completed';

    const eligibleIndexes = lines
        .map((l, i) => i)
        .filter(i => i !== sourceIndex)
        // pretend Unconfirmed_CM lines are not there
        .filter(i => (lines[i]?.Stage !== jobLineStages.Unconfirmed_CM))
        .filter(i => !sameOutcomeOnly || ((lines[i]?.Status ?? 'Completed') === sourceOutcome));

    const toggle = (i) => setSelected(prev => ({ ...prev, [i]: !prev[i] }));

    const selectAll = (e) => {
        const next = {};
        eligibleIndexes.forEach(i => { next[i] = true; });
        setSelected(next);
        e?.promise?.();
    };

    const clearAll = (e) => {
        setSelected({});
        e?.promise?.();
    };

    const doCopy = (e) => {
        const targetIndexes = eligibleIndexes.filter(i => !!selected[i]);
        onClose({ canceled: false, targetIndexes });
        e?.promise?.();
    };

    return (
        <AccentDialog
            headerContent="Copy values to lines"
            onClose={() => onClose({ canceled: true })}
            relativeWidth={d => Math.min(d.width - 80, 900)}
            actionsContent={[
                <AccentButton key="copy" outline={false} onClick={doCopy}>COPY</AccentButton>,
            ]}
        >
            <div className="mb-2">
                <strong>Source:</strong>{" "}
                {source?.JobLineNo} - {source?.LocationOther}: {source?.ProductName}
            </div>

            <Row className="mb-2">
                <Col xs="12" sm="6">
                    <label className="me-2"><strong>Same outcome only</strong></label>
                    <Input
                        type="checkbox"
                        checked={sameOutcomeOnly}
                        onChange={(e) => { setSameOutcomeOnly(e.target.checked); setSelected({}); }}
                    />
                </Col>
                <Col xs="12" sm="6" className="d-flex justify-content-end gap-2">
                    <AccentButton outline={false} onClick={selectAll}>SELECT ALL</AccentButton>
                    <AccentButton outline={false} onClick={clearAll}>CLEAR ALL</AccentButton>
                </Col>
            </Row>

            {eligibleIndexes.length === 0 && (
                <div>No eligible lines available.</div>
            )}

            {eligibleIndexes.length > 0 && (
                <div>
                    {eligibleIndexes.map(i => {
                        const l = lines[i];
                        return (
                            <div key={i} className="d-flex align-items-start mb-2">
                                <Input
                                    type="checkbox"
                                    checked={!!selected[i]}
                                    onChange={() => toggle(i)}
                                    style={{ marginTop: 4 }}
                                />
                                <div style={{ marginLeft: 10 }}>
                                    <strong>
                                        {l.JobLineNo} - {l.LocationOther}: {l.ProductName}
                                    </strong>
                                    <div className="text-muted">
                                        Status: <strong>{l.Status ?? 'Completed'}</strong>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </AccentDialog>
    );
};

const JobItemsList = ({ jobItems, rescheduleReasons, onChange }) => {
    const [items, setItems] = useState(
        jobItems.map((item) => ({
            ...item,
            Status: 'Completed',
            RescheduleReasonID: null,
            RescheduleNotes: null
        }))
    );

    // Only bump this when we need to force remount of "unbound" controls (copy),
    // not on every keystroke (otherwise notes loses focus).
    const [remountKey, setRemountKey] = React.useState(1);

    const canReschedule = (item) => item.Stage !== jobLineStages.Unconfirmed_CM;

    const pushChange = (updated) => {
        setItems(updated);
        onChange(updated);
    };

    const handleStatusChange = (itemID, newStatus) => {
        const updatedItems = items.map((item) => {
            if (item.ID !== itemID) return item;

            if (newStatus === 'Completed') {
                return { ...item, Status: 'Completed', RescheduleReasonID: null, RescheduleNotes: null };
            }

            return { ...item, Status: 'Reschedule' };
        });

        pushChange(updatedItems);
    };

    const handleReasonChange = (itemID, reasonID) => {
        const updated = items.map((item) =>
            item.ID === itemID ? { ...item, RescheduleReasonID: reasonID } : item
        );
        pushChange(updated);
    };

    const handleNotesChange = (itemID, notes) => {
        const updated = items.map((item) =>
            item.ID === itemID ? { ...item, RescheduleNotes: notes } : item
        );
        pushChange(updated);
    };

    const applyFromSourceToTargets = (sourceIndex, targetIndexes = null) => {
        const source = items[sourceIndex];
        if (!source) return;

        const sourceStatus = canReschedule(source) ? (source.Status ?? 'Completed') : 'Completed';

        const targetSet = (targetIndexes === null)
            ? new Set(items.map((_, i) => i).filter(i => i !== sourceIndex))
            : new Set(targetIndexes.filter(i => i !== sourceIndex));

        const updated = items.map((line, i) => {
            if (!targetSet.has(i)) return line;

            // Pretend Unconfirmed_CM lines are not there and force complete.
            if (!canReschedule(line)) {
                return {
                    ...line,
                    Status: 'Completed',
                    RescheduleReasonID: null,
                    RescheduleNotes: null
                };
            }

            // Always override
            if (sourceStatus === 'Completed') {
                return {
                    ...line,
                    Status: 'Completed',
                    RescheduleReasonID: null,
                    RescheduleNotes: null
                };
            }

            // Reschedule
            return {
                ...line,
                Status: 'Reschedule',
                RescheduleReasonID: source.RescheduleReasonID,
                RescheduleNotes: source.RescheduleNotes
            };
        });

        pushChange(updated);
        setRemountKey(x => x + 1);
    };

    const onCopyToAll = (sourceIndex) => applyFromSourceToTargets(sourceIndex, null);

    const onCopyTo = async (sourceIndex) => {
        const eligibleCount = items.filter(i => canReschedule(i)).length;
        if (eligibleCount <= 1) return;

        const result = await showDialog(<CopyToDlg lines={items} sourceIndex={sourceIndex} />);
        if (result?.canceled) return;

        const targets = result?.targetIndexes ?? [];
        if (targets.length === 0) return;

        applyFromSourceToTargets(sourceIndex, targets);
    };

    const showCopyButtons = items.filter(i => canReschedule(i)).length > 1;

    return (
        <Container>
            <Row>
                <Col>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Job Line No</th>
                                <th>Location</th>
                                <th>Product Name</th>
                                <th>Stage</th>
                                <th>Status</th>
                                <th>Copy</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => {
                                const allowReschedule = canReschedule(item);
                                const isReschedule = allowReschedule && (item.Status ?? 'Completed') === 'Reschedule';

                                return (
                                    <React.Fragment key={item.ID}>
                                        <tr>
                                            <td>{item.JobLineNo}</td>
                                            <td>{item.LocationOther}</td>
                                            <td>{item.ProductName}</td>
                                            <td>{t(item.Stage)}</td>
                                            <td>
                                                <div>
                                                    <AccentRadioUnboundCustom
                                                        key={`${remountKey}_${item.ID}_Completed_${item.Status ?? 'Completed'}`}
                                                        checked={(item.Status ?? 'Completed') === 'Completed'}
                                                        onChange={() => handleStatusChange(item.ID, 'Completed')}
                                                        label="Completed"
                                                        name={`statusGroup${item.ID}`}
                                                    />
                                                    {allowReschedule && (
                                                        <AccentRadioUnboundCustom
                                                            key={`${remountKey}_${item.ID}_Reschedule_${item.Status ?? 'Completed'}`}
                                                            checked={(item.Status ?? 'Completed') === 'Reschedule'}
                                                            onChange={() => handleStatusChange(item.ID, 'Reschedule')}
                                                            label="Reschedule"
                                                            name={`statusGroup${item.ID}`}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {showCopyButtons && allowReschedule && (
                                                    <div className="d-flex gap-2">
                                                        <AccentButton outline={false} onClick={() => onCopyToAll(idx)}>COPY TO ALL</AccentButton>
                                                        <AccentButton outline={false} onClick={() => onCopyTo(idx)}>COPY TO</AccentButton>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>

                                        {isReschedule && (
                                            <tr>
                                                <td colSpan={6}>
                                                    <div style={{ paddingLeft: 18 }}>
                                                        <div key={`${remountKey}_reason_${item.ID}`}>
                                                            <AccentComboUnbound
                                                                label="Reason"
                                                                displayField="Description"
                                                                valueField="ID"
                                                                required
                                                                defaultValue={item.RescheduleReasonID}
                                                                onChange={(v) => handleReasonChange(item.ID, v)}
                                                                data={rescheduleReasons}
                                                            />
                                                        </div>
                                                        <div key={`${remountKey}_notes_${item.ID}`}>
                                                            <AccentTextAreaUnbound
                                                                label="Notes"
                                                                defaultValue={item.RescheduleNotes}
                                                                onChange={(v) => handleNotesChange(item.ID, v)}
                                                                rows={2}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </Table>
                </Col>
            </Row>
        </Container>
    );
};


export const CMCompleteDlg = props => {

    const args = React.useRef({ id: props.jobID });
    const argsReschedule = React.useRef({ includeID: null, active: true });

    const lines = useQueryAllNoTracking("Job_CompleteCMLines", args.current);
    const reasons = useQueryAllNoTracking("PL_ActivityRescheduleReasons_SelectList", argsReschedule.current);

    const [cmLines, setCMLines] = React.useState([]);

    const allValid = React.useMemo(() => {
        const list = cmLines ?? [];
        return !list.some(l =>
            l.Stage !== jobLineStages.Unconfirmed_CM
            && l.Status === 'Reschedule'
            && accentUtils.isEmpty(l.RescheduleReasonID)
        );
    }, [cmLines]);

    const onOk = e => {
        props.onClose({
            canceled: false,
            cmLines: (cmLines ?? []).map(l => ({
                JobLineID: l.ID,
                Outcome: (l.Stage === jobLineStages.Unconfirmed_CM) ? 'Completed' : (l.Status ?? 'Completed'),
                RescheduleReasonID: (l.Stage === jobLineStages.Unconfirmed_CM || (l.Status ?? 'Completed') === 'Completed') ? null : l.RescheduleReasonID,
                RescheduleNotes: (l.Stage === jobLineStages.Unconfirmed_CM || (l.Status ?? 'Completed') === 'Completed') ? null : l.RescheduleNotes
            }))
        });

        e?.promise?.();
    };

    const onChange = e => {
        setCMLines(e);
    };


    let content = null;

    if (lines.loading || reasons.loading) {
        content = <AccentSpinner />;
    } else {
        content = <JobItemsList jobItems={lines.data} rescheduleReasons={reasons.data ?? []} onChange={onChange} />;
    }


    return <AccentDialog        
        onClose={props.onClose}
        headerContent={t("application_strings.application.dialogs.completeCMDlg.title")}
        actionsContent={[
            <AccentButton key="1" tagName="completeCMDlgClose" onClick={onOk} disabled={!allValid}>{t("application_strings.application.buttons.ok")}</AccentButton>
        ]}
    >
        { content}
    </AccentDialog>;

};